import React from "react";

// Render plain text or a special object with {type: 'vega-lite', spec, caption}
export const Response = ({ children }) => {
  if (typeof children === 'string') {
    const tryParse = (text) => {
      try {
        return JSON.parse(text);
      } catch {
        return null;
      }
    };

    // Remove code fences if present
    let raw = children.trim().replace(/^```json\s*/i, '').replace(/^```\s*/i, '').replace(/```\s*$/i, '').trim();

    // 1) Direct parse
    let parsed = (raw.startsWith('{') && raw.endsWith('}')) ? tryParse(raw) : null;

    // 2) Extract ALL embedded JSON objects that contain known artifact types
    const allArtifacts = [];
    if (!parsed) {
      const artifactRe = /"type"\s*:\s*"(vega-lite|suggestions|kpi-card|text-analysis)"/g;
      let artMatch;
      while ((artMatch = artifactRe.exec(raw)) !== null) {
        let start = raw.lastIndexOf('{', artMatch.index);
        if (start === -1) continue;
        let depth = 0;
        let end = -1;
        for (let i = start; i < raw.length; i++) {
          const ch = raw[i];
          if (ch === '{') depth++;
          else if (ch === '}') { depth--; if (depth === 0) { end = i; break; } }
        }
        if (end !== -1) {
          const obj = tryParse(raw.slice(start, end + 1));
          if (obj && obj.type) allArtifacts.push({ obj, start, end });
        }
      }
      if (allArtifacts.length === 1) parsed = allArtifacts[0].obj;
    }
    if (parsed && parsed.type === 'vega-lite' && parsed.spec) {
      try {
        const data = parsed.spec?.data?.values;
        
        if (data && Array.isArray(data) && data.length > 0) {
          const firstItem = data[0];
          const keys = Object.keys(firstItem);
          const hasDistributionData = keys.length >= 2 && data.length > 1;
          
          if (hasDistributionData) {
            // Extract question title and data
            const questionTitle = parsed.caption || parsed.spec.title || "Distribution";
            
            // Simple data extraction - just use the first string field as label and first number field as value
            const chartData = data.map((item) => {
              let label = 'Unknown';
              let value = 0;
              
              Object.keys(item).forEach(key => {
                const val = item[key];
                if (typeof val === 'string' && label === 'Unknown') {
                  label = val;
                } else if (typeof val === 'number' && value === 0) {
                  value = val;
                }
              });
              
              return { label, value };
            }).filter(item => item.value > 0);
            
            const totalResponses = chartData.reduce((sum, item) => sum + item.value, 0);
            
            // Add percentage calculation
            const chartDataWithPercentage = chartData.map(item => ({
              ...item,
              percentage: totalResponses > 0 ? ((item.value / totalResponses) * 100).toFixed(1) : '0.0'
            })).sort((a, b) => b.value - a.value);
            
            return (
              <div className="space-y-6 py-4">
                {/* Question header like Insights page */}
                <div className="flex items-start gap-3">
                  <div className="bg-blue-100 rounded-lg p-2 mt-1">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      {questionTitle}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {totalResponses} out of {totalResponses} people answered this question
                    </p>
                  </div>
                </div>
                
                {/* Distribution bars like Insights page */}
                <div className="space-y-3">
                  {chartDataWithPercentage.map((item, index) => (
                    <div key={index} className="flex items-center gap-4">
                      <div className="w-16 text-right text-lg font-medium text-gray-700">
                        {item.percentage}%
                      </div>
                      <div className="flex-1 bg-gray-100 rounded-lg h-12 relative overflow-hidden">
                        <div
                          className="bg-teal-500 h-full rounded-lg transition-all duration-1000 ease-out absolute top-0 left-0"
                          style={{ width: `${item.percentage}%` }}
                        />
                        <div className="absolute inset-0 flex items-center justify-between px-4">
                          <span className="text-gray-800 font-medium text-sm truncate flex-1">
                            {item.label}
                          </span>
                          <span className="text-gray-600 text-sm font-medium ml-3">
                            {item.value} response{item.value !== 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Followup content */}
                {parsed.followup && (
                  <div className="text-base text-gray-600 whitespace-pre-wrap leading-relaxed mt-6">
                    {parsed.followup}
                  </div>
                )}
                
                {Array.isArray(parsed.followupOptions) && parsed.followupOptions.length > 0 && (
                  <div className="flex flex-wrap gap-3 pt-2">
                    {parsed.followupOptions.map((opt, i) => (
                      <button 
                        key={i} 
                        type="button" 
                        className="text-sm rounded-full px-4 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 transition-colors font-medium" 
                        onClick={() => {
                          try { window.dispatchEvent(new CustomEvent('ai-followup-chip', { detail: opt })); } catch {}
                        }}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          }
        }
        
        // Fallback to regular Vega-Lite chart
        return (
          <div className="space-y-6 py-4">
            {parsed.spec.title && (
              <div className="text-lg font-semibold text-gray-800 mb-4">
                {typeof parsed.spec.title === 'string' ? parsed.spec.title : parsed.spec.title.text}
              </div>
            )}
            
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <VegaLite spec={parsed.spec} />
            </div>
            
            {parsed.caption && (
              <div className="text-base text-gray-700 leading-relaxed font-medium">
                {parsed.caption}
              </div>
            )}
            
            {parsed.followup && (
              <div className="text-base text-gray-600 whitespace-pre-wrap leading-relaxed">
                {parsed.followup}
              </div>
            )}
            
            {Array.isArray(parsed.followupOptions) && parsed.followupOptions.length > 0 && (
              <div className="flex flex-wrap gap-3 pt-2">
                {parsed.followupOptions.map((opt, i) => (
                  <button 
                    key={i} 
                    type="button" 
                    className="text-sm rounded-full px-4 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 transition-colors font-medium" 
                    onClick={() => {
                      try { window.dispatchEvent(new CustomEvent('ai-followup-chip', { detail: opt })); } catch {}
                    }}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            )}
          </div>
        );
        
      } catch (error) {
        console.error('Error rendering chart:', error);
        // Simple fallback
        return (
          <div className="space-y-6 py-4">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <VegaLite spec={parsed.spec} />
            </div>
          </div>
        );
      }
    }

    // Clean text by removing ALL artifact JSON from the display text
    let cleanText = raw;
    if (allArtifacts.length > 0) {
      // Remove each artifact's raw JSON from the text (process in reverse to keep indices valid)
      const sorted = [...allArtifacts].sort((a, b) => b.start - a.start);
      for (const art of sorted) {
        cleanText = cleanText.slice(0, art.start) + cleanText.slice(art.end + 1);
      }
      cleanText = cleanText.split('\n').filter(line => {
        const trimmed = line.trim();
        return trimmed && !trimmed.startsWith('{"type":');
      }).join('\n').trim();
    } else if (parsed) {
      const jsonStr = JSON.stringify(parsed);
      cleanText = cleanText.replace(jsonStr, '').trim();
      const lines = cleanText.split('\n').filter(line => {
        const trimmed = line.trim();
        return trimmed && !trimmed.startsWith('{"type":');
      }).join('\n').trim();
      cleanText = lines;
    }

    // Collect all artifact components to render
    const artifactComponents = [];
    const artifacts = allArtifacts.length > 0 ? allArtifacts.map(a => a.obj) : (parsed ? [parsed] : []);

    for (let ai = 0; ai < artifacts.length; ai++) {
      const art = artifacts[ai];
      if (!art || !art.type) continue;

      if (art.type === 'suggestions' && art.options) {
        artifactComponents.push(
          <div key={`suggestions-${ai}`} className="space-y-3 mt-4">
            {art.title && <h4 className="text-sm font-medium text-gray-700">{art.title}</h4>}
            <div className="flex flex-wrap gap-2">
              {art.options.map((opt, i) => (
                <button key={i} type="button" className="text-xs rounded-full px-3 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200" onClick={() => {
                  try { window.dispatchEvent(new CustomEvent('ai-followup-chip', { detail: opt })); } catch {}
                }}>{opt}</button>
              ))}
            </div>
          </div>
        );
      }

      if (art.type === 'kpi-card' && Array.isArray(art.cards)) {
        const iconMap = {
          star: (
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>
          ),
          gauge: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
          ),
          users: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
          ),
          chart: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
          ),
        };
        const colorMap = { green: 'bg-emerald-50 text-emerald-700 border-emerald-200', red: 'bg-red-50 text-red-700 border-red-200', blue: 'bg-blue-50 text-blue-700 border-blue-200', yellow: 'bg-amber-50 text-amber-700 border-amber-200' };
        const defaultColor = 'bg-gray-50 text-gray-700 border-gray-200';
        const trendArrow = { up: '↑', down: '↓', stable: '→' };

        artifactComponents.push(
          <div key={`kpi-${ai}`} className="grid grid-cols-2 sm:grid-cols-3 gap-3 py-4">
            {art.cards.map((card, ci) => (
              <div key={ci} className={`rounded-xl border px-4 py-3 flex flex-col gap-1 ${colorMap[card.color] || defaultColor}`}>
                <div className="flex items-center gap-2 text-sm font-medium opacity-80">
                  {iconMap[card.icon] || iconMap.chart}
                  <span>{card.label}</span>
                </div>
                <div className="text-2xl font-bold flex items-center gap-1">
                  {card.value}
                  {card.trend && <span className="text-sm font-medium opacity-60">{trendArrow[card.trend] || ''}</span>}
                </div>
              </div>
            ))}
          </div>
        );
      }

      if (art.type === 'text-analysis' && Array.isArray(art.themes)) {
        const sentimentColors = { positive: 'bg-emerald-100 text-emerald-800', negative: 'bg-red-100 text-red-800', neutral: 'bg-gray-100 text-gray-700', mixed: 'bg-amber-100 text-amber-800' };
        const isHe = /[\u0590-\u05FF]/.test(art.title || '');
        artifactComponents.push(
          <div key={`text-analysis-${ai}`} className="space-y-4 py-4" dir={isHe ? 'rtl' : 'ltr'}>
            {art.title && (
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" /></svg>
                <h3 className="text-lg font-semibold text-gray-900">{art.title}</h3>
              </div>
            )}
            {art.themes.map((theme, ti) => (
              <div key={ti} className="border border-gray-200 rounded-xl p-4 space-y-2">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-900">{theme.name}</span>
                    {theme.sentiment && (
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sentimentColors[theme.sentiment] || sentimentColors.neutral}`}>
                        {theme.sentiment}
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-500">
                    {theme.count != null && <span>{theme.count} responses</span>}
                    {theme.percentage != null && <span className="ml-1">({theme.percentage}%)</span>}
                  </div>
                </div>
                {Array.isArray(theme.quotes) && theme.quotes.length > 0 && (
                  <div className="space-y-1 mt-2">
                    {theme.quotes.slice(0, 3).map((quote, qi) => (
                      <div key={qi} className="text-sm text-gray-600 italic border-l-2 border-gray-300 pl-3 py-0.5">
                        "{quote}"
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      }
    }

    // Render regular text content if any
    const textComponent = cleanText ? (() => {
      const isHebrew = /[\u0590-\u05FF]/.test(cleanText);
      const html = toHtml(cleanText);
      return (
        <div 
          className={`leading-6 ${isHebrew ? 'text-right hebrew-text' : ''}`} 
          dir={isHebrew ? 'rtl' : 'ltr'} 
          style={{ 
            wordBreak: 'break-word',
            fontFamily: isHebrew ? "'Noto Sans Hebrew', 'Heebo', 'Assistant', sans-serif" : undefined,
            color: 'inherit'
          }} 
          dangerouslySetInnerHTML={{ __html: html }} 
        />
      );
    })() : null;

    // Return combined content: text + all artifacts
    if (textComponent || artifactComponents.length > 0) {
      return (
        <div className="space-y-2">
          {textComponent}
          {artifactComponents}
        </div>
      );
    }
    
    return null;
  }
  if (children && children.type === 'vega-lite' && children.spec) {
    return (
      <div className="space-y-2">
        <VegaLite spec={children.spec} />
        {children.caption && <div className="text-sm text-gray-600">{children.caption}</div>}
      </div>
    );
  }
  return <div className="whitespace-pre-wrap" style={{ color: 'inherit' }}>{children}</div>;
};

// Minimal Vega-Lite renderer using UMD bundles (avoids bundler peer-dep issues)
const loadScript = (src) => new Promise((resolve, reject) => {
  const existing = document.querySelector(`script[src="${src}"]`);
  if (existing) { existing.onload = resolve; return resolve(); }
  const s = document.createElement('script');
  s.src = src; s.async = true; s.onload = resolve; s.onerror = reject;
  document.body.appendChild(s);
});

const ensureVegaEmbed = async () => {
  if (window.vegaEmbed) return window.vegaEmbed;
  await loadScript('https://cdn.jsdelivr.net/npm/vega@5/build/vega.min.js');
  await loadScript('https://cdn.jsdelivr.net/npm/vega-lite@5/build/vega-lite.min.js');
  await loadScript('https://cdn.jsdelivr.net/npm/vega-embed@6/build/vega-embed.min.js');
  return window.vegaEmbed;
};

const applyVegaTheme = (spec) => {
  const themed = JSON.parse(JSON.stringify(spec || {}));
  
  // Force horizontal bar chart orientation like Insights page
  if (themed.mark && (themed.mark === 'bar' || (themed.mark.type && themed.mark.type === 'bar'))) {
    // Check if this is a vertical bar chart that needs to be converted to horizontal
    if (themed.encoding && themed.encoding.x && themed.encoding.y) {
      const xEncoding = themed.encoding.x;
      const yEncoding = themed.encoding.y;
      
      // If x is categorical and y is quantitative, swap them for horizontal bars
      if (
        (xEncoding.type === 'nominal' || xEncoding.type === 'ordinal') && 
        (yEncoding.type === 'quantitative')
      ) {
        themed.encoding.x = yEncoding;
        themed.encoding.y = xEncoding;
      }
      // If x has field like 'category'/'label' and y has field like 'value'/'count', swap them
      else if (
        (xEncoding.field && (xEncoding.field.includes('category') || xEncoding.field.includes('label') || xEncoding.field.includes('name'))) &&
        (yEncoding.field && (yEncoding.field.includes('value') || yEncoding.field.includes('count') || yEncoding.field.includes('frequency')))
      ) {
        themed.encoding.x = yEncoding;
        themed.encoding.y = xEncoding;
      }
    }
    
    // Create layered chart with text inside bars like Insights page
    if (themed.encoding && themed.encoding.y && themed.encoding.x) {
      // Convert to layered chart with bar + text
      const barLayer = {
        mark: {
          type: 'bar',
          cornerRadiusEnd: 8,
          tooltip: true,
          color: '#14B8A6', // Teal-500 like Insights
          opacity: 1
        },
        encoding: {
          x: themed.encoding.x,
          y: themed.encoding.y
        }
      };
      
      const textLayer = {
        mark: {
          type: 'text',
          align: 'left',
          baseline: 'middle',
          dx: 12, // More padding from left edge
          color: 'white',
          fontSize: 14,
          fontWeight: 500,
          font: 'Inter'
        },
        encoding: {
          x: { value: 0 }, // Start from left edge
          y: themed.encoding.y,
          text: {
            field: themed.encoding.y.field,
            type: 'nominal'
          }
        }
      };
      
      // Replace with layered spec
      themed.layer = [barLayer, textLayer];
      delete themed.mark;
      delete themed.encoding;
    }
  }
  
  // Clean white background
  themed.background = 'white';
  
  // Better sizing for chat context - compact like Insights
  themed.autosize = { type: 'fit', contains: 'padding' };
  if (!themed.width) themed.width = 500;
  if (!themed.height) themed.height = Math.max(250, (spec?.data?.values?.length || 4) * 50);
  
  // Configuration optimized for horizontal bar charts like Insights page
  const baseCfg = {
    view: { 
      stroke: 'transparent',
      continuousWidth: 500,
      continuousHeight: Math.max(250, (spec?.data?.values?.length || 4) * 50)
    },
    axis: {
      labelColor: 'transparent',
      titleColor: 'transparent',
      gridColor: 'transparent',
      tickColor: 'transparent',
      domainColor: 'transparent',
      labelFont: 'Inter',
      titleFont: 'Inter',
      labelFontSize: 0,
      titleFontSize: 0,
      titleFontWeight: 500,
      labelPadding: 0,
      titlePadding: 0,
      labelLimit: 0,
      grid: false,
      domain: false,
      ticks: false,
      labels: false
    },
    axisX: {
      labelColor: 'transparent', // Hide X axis completely like Insights
      titleColor: 'transparent',
      gridColor: 'transparent',
      tickColor: 'transparent',
      domainColor: 'transparent',
      labelFont: 'Inter',
      titleFont: 'Inter',
      labelFontSize: 0,
      titleFontSize: 0,
      titleFontWeight: 500,
      labelPadding: 0,
      titlePadding: 0,
      labelLimit: 0,
      grid: false,
      domain: false,
      ticks: false,
      labels: false,
      orient: 'bottom'
    },
    axisY: {
      labelColor: 'transparent', // Hide Y axis completely like Insights
      titleColor: 'transparent',
      gridColor: 'transparent',
      tickColor: 'transparent',
      domainColor: 'transparent',
      labelFont: 'Inter',
      titleFont: 'Inter',
      labelFontSize: 0,
      titleFontSize: 0,
      titleFontWeight: 500,
      labelPadding: 0,
      titlePadding: 0,
      labelLimit: 0,
      grid: false,
      domain: false,
      ticks: false,
      orient: 'left',
      labels: false
    },
    legend: {
      labelColor: '#374151',
      titleColor: '#111827',
      labelFont: 'Inter',
      titleFont: 'Inter',
      labelFontSize: 13,
      titleFontSize: 14,
      titleFontWeight: 500,
      padding: 8,
    },
    title: {
      fontSize: 16,
      fontWeight: 600,
      color: '#111827',
      anchor: 'start',
      offset: 16,
      font: 'Inter'
    },
    text: { 
      color: 'white', 
      font: 'Inter',
      fontSize: 13,
      fontWeight: 500,
      align: 'left',
      dx: 8 // Padding from left edge of bar
    },
    bar: { 
      cornerRadiusEnd: 6,
      color: '#14B8A6', // Teal color like Insights page
      opacity: 0.9
    },
    arc: { 
      cornerRadius: 4,
      stroke: 'white',
      strokeWidth: 1
    },
    point: {
      size: 80,
      filled: true,
      stroke: 'white',
      strokeWidth: 1
    },
    line: {
      strokeWidth: 2,
      stroke: '#14B8A6'
    }
  };
  
  themed.config = { ...baseCfg, ...(themed.config || {}) };
  
  // Preserve explicit colors if provided
  if (spec?.config?.bar?.color) {
    themed.config.bar.color = spec.config.bar.color;
  }
  
  return themed;
};

const VegaLite = ({ spec }) => {
  const ref = React.useRef(null);
  React.useEffect(() => {
    let mounted = true;
    ensureVegaEmbed()
      .then((embed) => {
        if (mounted && ref.current && embed) embed(ref.current, applyVegaTheme(spec), { actions: false, renderer: 'canvas' });
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, [spec]);
  return <div ref={ref} className="w-full min-h-[300px]" />;
};

// --- helpers: basic markdown-ish to HTML ---
const escapeHtml = (s) => s
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const toHtml = (text) => {
  const lines = escapeHtml(text).split(/\r?\n/);
  const parts = [];
  let inUl = false, inOl = false;
  const closeLists = () => {
    if (inUl) { parts.push('</ul>'); inUl = false; }
    if (inOl) { parts.push('</ol>'); inOl = false; }
  };

  for (let rawLine of lines) {
    const line = rawLine.trim();
    if (!line) { closeLists(); continue; }

    // headings ###, ##, #
    if (/^#{1,3}\s+/.test(line)) {
      closeLists();
      const level = (line.match(/^#{1,3}/)[0] || '#').length;
      const content = line.replace(/^#{1,3}\s+/, '');
      parts.push(`<h${level + 3} class="font-semibold text-gray-900">${applyInline(content)}</h${level + 3}>`);
      continue;
    }

    // ordered list 1. ...
    const olMatch = line.match(/^\d+\.\s+(.+)/);
    if (olMatch) {
      if (!inOl) { closeLists(); parts.push('<ol class="list-decimal ml-5 space-y-1">'); inOl = true; }
      parts.push(`<li>${applyInline(olMatch[1])}</li>`);
      continue;
    }

    // unordered list - ... or * ...
    const ulMatch = line.match(/^(?:-|\*)\s+(.+)/);
    if (ulMatch) {
      if (!inUl) { closeLists(); parts.push('<ul class="list-disc ml-5 space-y-1">'); inUl = true; }
      parts.push(`<li>${applyInline(ulMatch[1])}</li>`);
      continue;
    }

    // paragraph / media
    closeLists();
    const imgRe = /(https?:\/\/[^\s]+\.(?:png|jpg|jpeg|gif|webp)(?:\?[^\s]*)?)/gi;
    if (imgRe.test(line) && line.replace(imgRe, '').trim() === '') {
      // one or more image urls on this line
      const imgs = line.match(imgRe) || [];
      for (const src of imgs) {
        parts.push(`<div class="my-2"><img src="${escapeHtml(src)}" class="rounded-lg shadow border border-gray-200 max-w-full" loading="lazy"/></div>`);
      }
      continue;
    }
    parts.push(`<p>${applyInline(line)}</p>`);
  }
  closeLists();
  return parts.join('');
};

const applyInline = (s) => {
  // bold **text**
  let out = s.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  // simple emojis replacement
  out = out
    .replace(/:sparkles:/g, '✨')
    .replace(/:bulb:/g, '💡')
    .replace(/:chart:/g, '📊')
    .replace(/:graph:/g, '📈')
    .replace(/:idea:/g, '💭')
    .replace(/:check:/g, '✅');
  // linkify (non-image urls)
  const nonImgUrl = /(https?:\/\/[^\s]+?(?!(?:png|jpg|jpeg|gif|webp)(?:\?[^\s]*)?))/gi;
  out = out.replace(nonImgUrl, (match, url) => {
    const safeUrl = url.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    if (/^javascript:/i.test(safeUrl)) return url;
    return `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer" class="text-blue-600 underline">${url}</a>`;
  });
  return out;
};

